import { useCallback, useState, type FormEvent } from 'react';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import type { ZodIssue } from 'zod';
import { Button, Input, Label, Modal } from '@immo/ui-kit';
import {
  UpdateAdminOrganizationAddressBodySchema,
  UpdateAdminOrganizationNameBodySchema,
  type GetAdminOrganizationResponse,
} from '@immo/api-contract';
import { AddressAutocomplete } from '@immo/components';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { env } from '@/lib/env';
import type { ParsedAddress } from '@immo/components';

type Field = 'name' | 'street' | 'houseNumber' | 'postalCode' | 'city' | 'country';

type FormErrors = Readonly<Record<Field, string | null>>;

const NO_ERRORS: FormErrors = {
  name: null,
  street: null,
  houseNumber: null,
  postalCode: null,
  city: null,
  country: null,
};

function fieldFromPath(path: ReadonlyArray<PropertyKey>): Field | undefined {
  const [head, sub] = path;

  if (head === 'name') return 'name';

  if (head === 'address') {
    if (sub === 'street') return 'street';

    if (sub === 'houseNumber') return 'houseNumber';

    if (sub === 'postalCode') return 'postalCode';

    if (sub === 'city') return 'city';

    if (sub === 'country') return 'country';
  }

  return undefined;
}

function issueKey(field: Field, issue: ZodIssue, rawInput: string): string {
  if (field === 'postalCode') return rawInput.trim().length === 0 ? 'required' : 'invalid';

  if (issue.code === 'too_small') return 'required';

  if (issue.code === 'too_big') return 'too-long';

  return 'invalid';
}

export async function organizationEditLoader({ params }: LoaderFunctionArgs): Promise<GetAdminOrganizationResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing organization id', { status: 404 });
  }

  const result = await apiClient.adminOrganizations.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load organization', { status: result.status });
  }

  return result.body;
}

type Row = {
  id: string;
  field: Field;
  label: string;
  inputMode?: 'numeric';
};

export function OrganizationEditPage() {
  const t = useTranslations('pages.admin.organizations.edit');
  const organization = useLoaderData() as GetAdminOrganizationResponse;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [name, setName] = useState(organization.name);
  const [street, setStreet] = useState(organization.address.street);
  const [houseNumber, setHouseNumber] = useState(organization.address.houseNumber);
  const [postalCode, setPostalCode] = useState(organization.address.postalCode);
  const [city, setCity] = useState(organization.address.city);
  const [country, setCountry] = useState(organization.address.country);
  const [latitude, setLatitude] = useState<number | null>(organization.address.latitude);
  const [longitude, setLongitude] = useState<number | null>(organization.address.longitude);
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setters: Record<Field, (v: string) => void> = {
    name: setName,
    street: setStreet,
    houseNumber: setHouseNumber,
    postalCode: setPostalCode,
    city: setCity,
    country: setCountry,
  };
  const values: Record<Field, string> = { name, street, houseNumber, postalCode, city, country };

  const clearError = (field: Field) => setErrors((prev) => (prev[field] === null ? prev : { ...prev, [field]: null }));

  const applyAutocomplete = useCallback((address: ParsedAddress) => {
    setStreet(address.street);
    setHouseNumber(address.houseNumber);
    setPostalCode(address.postalCode);
    setCity(address.city);
    setLatitude(address.latitude);
    setLongitude(address.longitude);

    if (address.country !== '') {
      setCountry(address.country);
    }

    setErrors(NO_ERRORS);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericError(null);

    const nameParsed = UpdateAdminOrganizationNameBodySchema.safeParse({ name });
    const addressParsed = UpdateAdminOrganizationAddressBodySchema.safeParse({
      address: { street, houseNumber, postalCode, city, country, latitude, longitude },
    });

    if (!nameParsed.success || !addressParsed.success) {
      const next: Record<Field, string | null> = { ...NO_ERRORS };

      const issues = [
        ...(nameParsed.success ? [] : nameParsed.error.issues),
        ...(addressParsed.success ? [] : addressParsed.error.issues),
      ];

      for (const issue of issues) {
        const field = fieldFromPath(issue.path);

        if (field !== undefined && next[field] === null) {
          const labelSlug = field === 'houseNumber' ? 'house-number' : field === 'postalCode' ? 'postal-code' : field;
          next[field] = t(`errors.${labelSlug}.${issueKey(field, issue, values[field])}`);
        }
      }

      setErrors(next);

      return;
    }

    setSubmitting(true);

    try {
      if (nameParsed.data.name !== organization.name) {
        const nameResult = await apiClient.adminOrganizations.updateName({
          pathParams: { id: organization.id },
          body: nameParsed.data,
        });

        if (!nameResult.ok) {
          setGenericError(t(nameResult.status === 403 ? 'forbidden-error' : 'generic-error'));

          return;
        }
      }

      const result = await apiClient.adminOrganizations.updateAddress({
        pathParams: { id: organization.id },
        body: addressParsed.data,
      });

      if (result.ok) {
        revalidator.revalidate();
        navigate(`/workspaces/organizations/${organization.id}`);

        return;
      }

      setGenericError(t(result.status === 403 ? 'forbidden-error' : 'generic-error'));
    } catch {
      setGenericError(t('generic-error'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (row: Row) => (
    <div key={row.id} className="space-y-1">
      <Label htmlFor={row.id}>{row.label}</Label>
      <Input
        id={row.id}
        value={values[row.field]}
        onChange={(e) => {
          setters[row.field](e.target.value);
          clearError(row.field);
        }}
        disabled={submitting}
        invalid={errors[row.field] !== null}
        inputMode={row.inputMode}
      />
      {errors[row.field] !== null && <p className="text-sm text-danger-600">{errors[row.field]}</p>}
    </div>
  );

  const close = () => navigate(`/workspaces/organizations/${organization.id}`);

  return (
    <Modal
      open
      onClose={close}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('back-cta')}
          </Button>
          <Button type="submit" form="organization-edit-form" disabled={submitting}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="organization-edit-form" className="space-y-4" onSubmit={submit} noValidate>
        {renderField({ id: 'name', field: 'name', label: t('name-label') })}
        <AddressAutocomplete
          apiKey={env.googleMapsApiKey}
          label={t('address-search-label')}
          placeholder={t('address-search-placeholder')}
          disabled={submitting}
          onPlaceSelected={applyAutocomplete}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_120px]">
          {renderField({ id: 'street', field: 'street', label: t('street-label') })}
          {renderField({ id: 'house-number', field: 'houseNumber', label: t('house-number-label') })}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[120px_1fr]">
          {renderField({
            id: 'postal-code',
            field: 'postalCode',
            label: t('postal-code-label'),
            inputMode: 'numeric',
          })}
          {renderField({ id: 'city', field: 'city', label: t('city-label') })}
        </div>
        {renderField({ id: 'country', field: 'country', label: t('country-label') })}
        {genericError !== null && <p className="text-base text-danger-600">{genericError}</p>}
      </form>
    </Modal>
  );
}
